import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import {
  ArrowUpRight, ChevronDown, Code2, Database, FileSpreadsheet, Globe, Landmark,
  Layout, Lock, Megaphone, Menu, Repeat2, Rocket, Server, ShieldAlert, Sparkles,
  Star, Users, Waves, X,
} from "lucide-react";
import {
  BRAND, FUNDED_FORM_DROPDOWN, FX_DROPDOWN, NAV_ITEMS, SERVICES_DROPDOWN,
  dropdownItemPath, navItemPath,
} from "../lib/constants";

const ICONS = {
  Sparkles, Layout, Waves, Lock, Repeat2, Database, Globe, Server, Rocket,
  Code2, Megaphone, FileSpreadsheet, Landmark, ShieldAlert, Users, UserStar: Star,
};

const MENUS = {
  fx: FX_DROPDOWN,
  funded: FUNDED_FORM_DROPDOWN,
  services: SERVICES_DROPDOWN,
};

function MegaMenu({ columns, close, title }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 6 }}
      transition={{ duration: 0.16 }}
      className="fixed left-4 right-4 top-[76px] mx-auto max-w-[1120px] overflow-hidden rounded-b-[2rem] border border-t-2 border-white/10 border-t-[#D6B86A] bg-[#0B1711]/[0.99] shadow-[0_32px_100px_rgba(0,0,0,.62)]"
    >
      <div className="grid lg:grid-cols-[250px_1fr]">
        <div className="flex flex-col justify-between border-b border-white/10 bg-[#102219] p-7 lg:border-b-0 lg:border-r">
          <div>
            <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#D6B86A]">Explore</span>
            <h2 className="mt-3 font-display text-2xl font-bold text-white">{title}</h2>
            <p className="mt-3 text-xs leading-relaxed text-white/40">
              Specialist tools and delivery services for modern broker operations.
            </p>
          </div>
          <Link to="/contact" onClick={close} className="mt-7 inline-flex items-center gap-2 text-xs font-bold text-[#D6B86A]">
            Discuss your requirements <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        </div>

        <div className="grid gap-8 p-7 md:grid-cols-2 lg:grid-cols-3">
          {columns.map((column) => (
            <div key={column.group}>
              <p className="mb-3 border-b border-white/10 pb-3 font-mono text-[9px] uppercase tracking-[0.24em] text-white/30">
                {column.group}
              </p>
              <div className="space-y-1">
                {column.items.map((item) => {
                  const Icon = ICONS[item.icon] || Sparkles;
                  return (
                    <Link
                      key={item.title}
                      to={dropdownItemPath(item)}
                      onClick={close}
                      className="group flex items-center gap-3 rounded-xl py-2.5 text-sm font-semibold text-white/70 transition hover:text-white"
                    >
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-white/10 text-[#D6B86A] transition group-hover:border-[#D6B86A]/40 group-hover:bg-[#D6B86A]/10">
                        <Icon className="h-3.5 w-3.5" />
                      </span>
                      <span>{item.title}</span>
                      <ArrowUpRight className="ml-auto h-3 w-3 opacity-0 transition group-hover:opacity-60" />
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
}

export default function Header() {
  const [menu, setMenu] = useState(null);
  const [mobile, setMobile] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMenu(null);
    setMobile(false);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    document.body.style.overflow = mobile ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobile]);

  return (
    <>
      <header className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-[#07110C]/95 backdrop-blur-xl" data-testid="site-header">
        <div className="relative mx-auto flex h-[76px] max-w-7xl items-center gap-6 px-6">
          <Link to="/" className="shrink-0" data-testid="logo-link">
            <img src={BRAND.logo} alt={BRAND.name} className="h-11 w-auto" />
          </Link>

          <nav className="relative hidden h-full flex-1 items-center justify-center gap-7 lg:flex">
            {NAV_ITEMS.map((item, index) => {
              const hasMenu = Boolean(item.dropdownId);
              const active = menu === item.dropdownId;
              return (
                <div
                  key={item.label}
                  onMouseEnter={() => hasMenu && setMenu(item.dropdownId)}
                  onMouseLeave={() => hasMenu && setMenu(null)}
                  className="relative"
                >
                  <Link
                    to={navItemPath(item)}
                    className={`group relative flex h-[76px] items-center gap-2 text-[13px] font-semibold transition ${
                      active ? "text-white" : "text-white/55 hover:text-white"
                    }`}
                  >
                    <span className="font-mono text-[8px] text-[#D6B86A]/70">0{index + 1}</span>
                    <span>{item.label}</span>
                    {hasMenu && <ChevronDown className="h-3.5 w-3.5 opacity-50" />}
                    <span className={`absolute inset-x-0 bottom-0 h-0.5 bg-[#D6B86A] transition-transform ${active ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"}`} />
                  </Link>
                  <AnimatePresence>
                    {active && <MegaMenu columns={MENUS[item.dropdownId]} title={item.label} close={() => setMenu(null)} />}
                  </AnimatePresence>
                </div>
              );
            })}
          </nav>

          <Link
            to="/contact"
            className="ml-auto hidden items-center gap-3 border-l border-white/10 pl-6 text-sm font-bold text-[#D6B86A] sm:flex"
            data-testid="header-cta"
          >
            Start a brief <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#D6B86A] text-[#07110C]"><ArrowUpRight className="h-4 w-4" /></span>
          </Link>
          <button
            type="button"
            onClick={() => setMobile(true)}
            className="ml-auto rounded-xl border border-white/10 p-3 text-white lg:hidden"
            aria-label="Open navigation"
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {mobile && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-[#07110C] p-6 lg:hidden"
          >
            <div className="flex items-center justify-between">
              <img src={BRAND.logo} alt={BRAND.name} className="h-12 w-auto" />
              <button onClick={() => setMobile(false)} className="rounded-xl border border-white/10 p-3 text-white" aria-label="Close navigation">
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="mt-14">
              {NAV_ITEMS.map((item, index) => (
                <Link
                  key={item.label}
                  to={navItemPath(item)}
                  className="flex items-center justify-between border-b border-white/10 py-5 font-display text-2xl font-bold text-white"
                >
                  <span>{item.label}</span>
                  <span className="font-mono text-xs text-[#C8FF4D]">0{index + 1}</span>
                </Link>
              ))}
            </nav>
            <Link to="/contact" className="mt-10 flex items-center justify-center gap-2 rounded-2xl bg-[#C8FF4D] px-6 py-4 font-extrabold text-[#07110C]">
              Start a project <ArrowUpRight className="h-4 w-4" />
            </Link>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
